import { Component, OnInit, Inject, Type } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AngularMaterialModule } from 'src/app/material.module';
import { AccountsService, SidePanelService } from '@lib/services';
import { ApiResponseError, IAccount, IAuthResponse, IFormGroupSchema, IUser } from '@lib/interfaces';
import { BaseComponent } from '@lib/components';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TransferService } from '@lib/services/fund-transfer/transfer.service';
import { BillPaymentService } from '@lib/services/bill-payments/bill-payment.service';
import { IFundTransferResponse, IBookResponse } from '@lib/interfaces/transfer.interface';
import { finalize, debounceTime, switchMap, distinctUntilChanged, EMPTY, catchError } from 'rxjs';
import { CustomersService } from '@lib/services/customer/customer.service';
import { maskFullName } from '@lib/utils';
import { ETransferFlow } from '@pages/send-money/tenant6/components/transfer-money/transfer-money.component';
import { PeerToPeerSuccessComponent } from '@pages/send-money/tenant6/components/peer-to-peer-success/peer-to-peer-success.component';

interface IWalletInfo {
    balance: number;
    walletId: string;
}
interface IScreenForm {
    note: string;
    senderCurrency: string;
    receiverCurrency: string;
    sendAmount: number;
    receiveAmount: number;
}
@Component({
    template: '',
})
export class SharedPeerToPeerComponent extends BaseComponent implements OnInit {
    recipientInfo: IUser; // receiver's info
    userInfo: IAuthResponse['admin'] = JSON.parse(localStorage.getItem('userInfo') || ''); // sender info
    senderAccounts: Array<IAccount> = [];
    recipientAccounts: Array<IAccount> = [];
    form: FormGroup<IFormGroupSchema<IScreenForm>>;
    senderWalletInfo: Map<string, IWalletInfo> = new Map();
    ReceiverWalletInfo: Map<string, IWalletInfo> = new Map();
    loading = false;
    redirectToComponent: Type<any>;
    exchangeDetails: any;
    protected transferType: typeof ETransferFlow = ETransferFlow;

    constructor(
        public dialogRef: MatDialogRef<SharedPeerToPeerComponent>,
        private readonly sidePanel: SidePanelService,
        private snackbar: MatSnackBar,
        private readonly formBuilder: FormBuilder,
        private transferService: TransferService,
        private readonly billPaymentService: BillPaymentService,
        private customersService: CustomersService,
        private accountService: AccountsService,
        @Inject(MAT_DIALOG_DATA) public data: { selectedTransferType: ETransferFlow },
    ) {
        super();
    }

    ngOnInit() {
        if (this.data.selectedTransferType === this.transferType.INTRA_WALLET) {
            this.fetchUserDetails();
        } else {
            this.processAccountInfo();
        }
        this.initForm();
        this.calculateFxRate();
        this.form.controls.senderCurrency.valueChanges.subscribe((val) => {
            this.form.controls.receiveAmount.reset();
            this.form.controls.sendAmount.reset();
            this.exchangeDetails = null;
        });
    }

    fetchUserDetails() {
        this.addSubscription(
            this.accountService.getAccountsByUserId(this.userInfo.id).subscribe({
                next: ({ data: accounts }) => {
                    this.form.controls.senderCurrency.setValue(accounts[0]?.currencies[0]);
                    this.form.controls.receiverCurrency.setValue(accounts[0]?.currencies[0]);
                    this.senderAccounts = accounts;
                    this.recipientAccounts = accounts;
                    this.processAccountInfo();
                },
                error: (error: ApiResponseError) => {
                    this.createApiErrorSnackBar(this.snackbar, error);
                },
            }),
            this.customersService.fetchCustomerDetailsById(this.userInfo.id).subscribe((response: any) => {
                this.userInfo = response;
                this.recipientInfo = response;
            }),
        );
    }

    calculateFxRate() {
        this.form.controls.sendAmount.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                switchMap((val) => {
                    if (this.form.controls.receiverCurrency.value === this.form.controls.senderCurrency.value) {
                        this.exchangeDetails = { exchange_rate: 1 };
                        this.form.controls.receiveAmount.setValue(val);
                        return EMPTY;
                    }
                    return this.billPaymentService
                        .getFxRateQuote({
                            account_id: this.senderWalletInfo.get(this.form.controls.senderCurrency.value)?.walletId || '',
                            recipient_account_id: this.ReceiverWalletInfo.get(this.form.controls.receiverCurrency.value)?.walletId || '',
                            amount: val,
                            from_currency: this.form.controls.senderCurrency.value,
                            to_currency: this.form.controls.receiverCurrency.value,
                        })
                        .pipe(
                            catchError((err) => {
                                return EMPTY;
                            }),
                        );
                }),
            )
            .subscribe({
                next: (response: any) => {
                    this.exchangeDetails = response;
                    this.form.controls.receiveAmount.setValue(response?.to_amount);
                },
                error: (err) => {
                    console.error('Error fetching FX rate:', err);
                },
            });
    }

    processAccountInfo() {
        this.senderAccounts.map((account) => {
            const currency = Object.keys(account.balances)[0];
            this.senderWalletInfo.set(currency, {
                balance: account.balances[currency],
                walletId: account.id,
            });
        });

        this.recipientAccounts.map((account) => {
            const currency = Object.keys(account.balances)[0];
            this.ReceiverWalletInfo.set(currency, {
                balance: account.balances[currency],
                walletId: account.id,
            });
        });
    }

    private initForm() {
        this.form = new FormGroup({
            senderCurrency: this.formBuilder.nonNullable.control<string>(this.senderAccounts[0]?.currencies[0], [Validators.required]),
            receiverCurrency: this.formBuilder.nonNullable.control<string>(this.recipientAccounts[0]?.currencies[0], [Validators.required]),
            note: this.formBuilder.nonNullable.control<string>(''),
            sendAmount: this.formBuilder.nonNullable.control<number>(0, [Validators.required, Validators.min(0.001)]),
            receiveAmount: this.formBuilder.nonNullable.control<number>(0, [Validators.required, Validators.min(0.001)]),
        });
    }

    protected bookFxRate() {
        this.redirectToComponent = PeerToPeerSuccessComponent;
        this.addSubscription(
            this.transferService
                .bookFxRate(this.exchangeDetails?.id)
                .pipe(
                    finalize(() => {
                        this.loading = false;
                    }),
                )
                .subscribe({
                    next: (response: IBookResponse) => {
                        this.dialogRef.close();
                        const dialogRef = this.sidePanel.open(this.redirectToComponent);
                        dialogRef.componentInstance.recipientInfo = this.recipientInfo;
                        dialogRef.componentInstance.userInfo = this.userInfo;
                        dialogRef.componentInstance.transferResponse = response;
                    },
                    error: (error: ApiResponseError) => {
                        this.createApiErrorSnackBar(this.snackbar, error);
                    },
                }),
        );
    }

    protected continueToPeerToPeerConfirmation() {
        if (this.exchangeDetails?.exchange_rate !== 1) {
            this.bookFxRate();
            return;
        }

        this.loading = true;
        this.addSubscription(
            this.transferService
                .fundTransfer({
                    sender_account_id: this.senderWalletInfo.get(this.form.controls.senderCurrency.value)?.walletId || '',
                    recipient_account_id: this.ReceiverWalletInfo.get(this.form.controls.receiverCurrency.value)?.walletId || '',
                    amount: this.form.controls.sendAmount.value,
                    currency: this.form.controls.senderCurrency.value,
                    notes: this.form.controls.note.value,
                    dry_run: true,
                })
                .pipe(
                    finalize(() => {
                        this.loading = false;
                    }),
                )
                .subscribe({
                    next: (response: IFundTransferResponse) => {
                        this.dialogRef.close();
                        const dialogRef = this.sidePanel.open(this.redirectToComponent);
                        dialogRef.componentInstance.recipientInfo = this.recipientInfo;
                        dialogRef.componentInstance.userInfo = this.userInfo;
                        dialogRef.componentInstance.transferResponse = response;
                    },
                    error: (error: ApiResponseError) => {
                        this.createApiErrorSnackBar(this.snackbar, error);
                    },
                }),
        );
    }

    backToBeneficiarySelection() {
        this.dialogRef.close();
        // this.sidePanel.open(BeneficiarySelectionComponent);
    }

    get balance() {
        const curr = this.form.controls.senderCurrency.value;
        return this.senderWalletInfo.get(curr)?.balance ?? 0;
    }

    get doesAmountExceedBalance(): boolean {
        return this.balance < this.form.controls.sendAmount.value;
    }

    get isCurrencySame(): boolean {
        if (this.form.controls.senderCurrency.value === 'SDG') {
            return this.form.controls.receiverCurrency.value === this.form.controls.senderCurrency.value;
        }
        return true;
    }

    // NEOIQ-2427: shared with the Top-up for Agent flow via @lib/utils — replaces the inline
    // template expression (`'*'.repeat(name.length - 2)`) that throws a RangeError for names of
    // length <= 2. Templates should use this getter instead of masking inline.
    get maskedRecipientName(): string {
        return maskFullName(this.recipientInfo?.first_name, this.recipientInfo?.last_name);
    }
}
